import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Button,
  Box,
  Grid,
  Chip,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useThunkDispatch, useTypedSelector } from 'hooks';
import { Styles, StyledTableCell, StyledTableRow, Alert } from 'components/Tools';
import {
  fetchComisionistList,
  clearComisionistDetail,
  fetchComisionistStats,
  fetchComisionistDetail,
} from 'redux/actions/comisionist.actions';
import {
  ComisionistPrimaryStatusChip,
  ComisionistSecondaryStatusChip,
} from './ComisionistStatusChips';
import { formatDateDDMMYYYY } from 'utils/dateFormatter';
import { FilterComisionist } from './FilterComisionist';
import { DetailsComisionist } from './DetailsComisionist';
import ModalCustom from 'components/Tools/ModalCustom';
import { Loading } from 'components/Tools/Loading';
import Pagination from '@material-ui/lab/Pagination';
import { Routes } from 'config/routes';
import './comisionist.css';

const PAGE_SIZE = 10;

export function ListComisionist() {
  const classes = Styles();
  const dispatch = useThunkDispatch();
  const history = useHistory();

  const { list, filter, isLoading, error, stats } = useTypedSelector((s) => s.comisionist);
  const [page, setPage] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [minLoadingComplete, setMinLoadingComplete] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isLoading) {
      setMinLoadingComplete(false);
      timer = setTimeout(() => {
        setMinLoadingComplete(true);
      }, 3000);
    } else if (!isLoading) {
      const checkAndComplete = () => {
        if (!isLoading) {
          setMinLoadingComplete(true);
        }
      };
      checkAndComplete();
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading]);

  // Cargar lista de comisionistas
  useEffect(() => {
    dispatch(
      fetchComisionistList({
        page,
        size: PAGE_SIZE,
        filter,
      }),
    );
  }, [dispatch, page, filter]);

  // Reload stats whenever the active filter changes
  useEffect(() => {
    dispatch(fetchComisionistStats(filter));
  }, [dispatch, filter]);

  // Manejo de vista de detalle
  const handleViewDetail = (comisionistId: string | undefined) => {
    if (comisionistId) {
      dispatch(fetchComisionistDetail(comisionistId));
      setShowDetail(true);
    }
  };

  const handleCloseDetail = () => {
    dispatch(clearComisionistDetail());
    setShowDetail(false);
  };

  const handleNewComisionist = () => {
    history.push(`${Routes.comisionistForm}?mode=crear`);
  };

  const handleEditComisionist = (comisionistId: string | undefined) => {
    history.push(`${Routes.comisionistForm}?mode=editar&id=${comisionistId}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Conteo de estados - usar stats del API si están disponibles, sino calcular localmente
  const totalComisionistas = stats?.total ?? list.meta?.total ?? 0;
  const activos = stats?.active ?? list.comisionistas.filter((c) => c.status === 'Active').length;
  const nuevos = stats?.new ?? list.comisionistas.filter((c) => c.status === 'New').length;
  const inactivos = stats?.inactive ?? list.comisionistas.filter((c) => c.status === 'Inactive').length;
  const pendFinanzas = stats?.pendingFinance ?? list.comisionistas.filter((c) => c.documentsStatus === 'PendingVerification').length;
  const pendDocumentos = stats?.pendingDocuments ?? list.comisionistas.filter((c) => c.documentsStatus === 'PendingDocuments').length;
  const docsOk = stats?.docsOk ?? 0;

  const showLoading = !minLoadingComplete || isLoading;

  return (
    <div className="comisionist-container">
      {/* Header */}
      <div className="page-header-comisionist">
        <div className="header-title">
          <h1>Comisionistas</h1>
        </div>
      </div>

      {/* Alert */}
      {(!showLoading) && (pendFinanzas > 0 || pendDocumentos > 0) && (
        <Box mx={2} mt={2}>
          <Alert severity="warning">
            <strong>{pendFinanzas}</strong> registros pendientes de verificación de Finanzas · <strong>{pendDocumentos}</strong>{' '}
            registros con documentos incompletos
          </Alert>
        </Box>
      )}

      {/* Filtros */}
      <FilterComisionist />

      {showLoading ? (
        <Loading
          message="Cargando comisionistas..."
          subMessage="Por favor espere mientras se cargan los comisionistas"
        />
      ) : (
        <>
      {/* Summary Cards */}
      <Grid container style={{ paddingInline: '16px' , gap: '5px', justifyContent: 'center' }}>
        <Grid item xs={12} sm={5} md={2}>
          <div className="summary-card">
            <div className="summary-value">{totalComisionistas}</div>
            <div className="summary-label">Total Comisionistas</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={1}>
          <div className="summary-card ">
            <div className="summary-value" style={{ color: '#4eab54' }}>
              {activos}
            </div>
            <div className="summary-label">Activos</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={1}>
          <div className="summary-card">
            <div className="summary-value" style={{ color: '#2d90e1' }}>
              {nuevos}
            </div>
            <div className="summary-label">Nuevos</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={1}>
          <div className="summary-card">
            <div className="summary-value" style={{ color: '#757575' }}>
              {inactivos}
            </div>
            <div className="summary-label">Inactivos</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={2}>
          <div className="summary-card">
            <div className="summary-value" style={{ color: '#fabf03' }}>
              {pendFinanzas}
            </div>
            <div className="summary-label">Pend. Verificación</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={2}>
          <div className="summary-card">
            <div className="summary-value" style={{color: '#e03b3e'}}>{pendDocumentos}</div>
            <div className="summary-label">Pend. Documentos</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={5} md={1}>
          <div className="summary-card summary-green">
            <div className="summary-value">{docsOk}</div>
            <div className="summary-label">Docs Ok</div>
          </div>
        </Grid>
      </Grid>

      {/* Tabla */}
      <Paper className={classes.paper} style={{ margin: '16px', overflow: 'auto' }}>
        <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
          <span>
            Mostrando{' '}
            <strong>
              {list.comisionistas.length} de {list.meta?.total || 0}
            </strong>{' '}
            registros
          </span>
         {/*  <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleNewComisionist}
          >
            Nuevo Comisionista
          </Button> */}
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>CUENTA</StyledTableCell>
                <StyledTableCell>NOMBRE COMISIONISTA</StyledTableCell>
                <StyledTableCell>TIPO</StyledTableCell>
                <StyledTableCell>DUI</StyledTableCell>
                <StyledTableCell>NIT</StyledTableCell>
                <StyledTableCell>ESTADO CLIENTE</StyledTableCell>
                <StyledTableCell>ESTADO DOCUMENTAL</StyledTableCell>
                <StyledTableCell>FECHA ALTA AS400</StyledTableCell>
                <StyledTableCell>SINCRONIZADO</StyledTableCell>
                <StyledTableCell align="center">ACCIONES</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.comisionistas.map((comisionist) => (
                <StyledTableRow key={comisionist.id}>
                  <StyledTableCell>{comisionist.account}</StyledTableCell>
                  <StyledTableCell>
                    <strong>{comisionist.name}</strong>
                  </StyledTableCell>
                  <StyledTableCell>{comisionist.type}</StyledTableCell>
                  <StyledTableCell>{comisionist.dui || '—'}</StyledTableCell>
                  <StyledTableCell>{comisionist.nit}</StyledTableCell>
                  <StyledTableCell align='center' size='small'>
                    <ComisionistPrimaryStatusChip status={comisionist.status} />
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    <ComisionistSecondaryStatusChip comisionist={comisionist} />
                  </StyledTableCell>
                  <StyledTableCell align='center' size='small'>
                    {formatDateDDMMYYYY(comisionist.dateHighAS400)}
                  </StyledTableCell>
                  <StyledTableCell>
                    {comisionist.synchronized ? (
                      <Chip
                        label={`✔ ${formatDateDDMMYYYY(comisionist.dateSynchronized)}`}
                        size="small"
                        style={{
                          backgroundColor: '#e8f5e9',
                          color: '#00695c',
                          border: '1px solid #81c784',
                          fontWeight: 'bold',
                        }}
                      />
                    ) : (
                      '—'
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Box display="flex" justifyContent="center" style={{ gap: '6px' }}>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleEditComisionist(comisionist.id)}
                      >
                        Ver
                      </Button>
                     {/*  <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditComisionist(comisionist.id)}
                      >
                        Editar
                      </Button> */}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Paginación */}
      {list.meta && list.meta.pages > 1 && (
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={list.meta.pages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
        </>
      )}

      {/* Modal Detalle */}
      <ModalCustom open={showDetail} onClose={handleCloseDetail}>
        <DetailsComisionist onClose={handleCloseDetail} />
      </ModalCustom>
    </div>
  );
}
